// src/pages/WorldCup.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import { rugbyFlagPath } from "../utils/flags";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExploreIcon from "@mui/icons-material/Explore";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SportsRugbyOutlinedIcon from "@mui/icons-material/SportsRugbyOutlined";
import SouthAmericaOutlinedIcon from "@mui/icons-material/SouthAmericaOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

// NEW palette
const BRAND = {
  bg: "#EEF5FF",
  panelBg: "#B4D4FF",
  border: "#86B6F6",
  ink: "#0B2230",
  accent: "#176B87",
  chipBg: "rgba(238,245,255,0.85)",
  // Winner highlight
  winBg: "rgba(46, 204, 113, 0.18)",
  winBorder: "rgba(46, 204, 113, 0.55)",
  winInk: "#0B2230",
  loseBg: "rgba(231, 76, 60, 0.10)",
  loseBorder: "rgba(231, 76, 60, 0.25)",
  drawBg: "rgba(241, 196, 15, 0.16)",
  drawBorder: "rgba(241, 196, 15, 0.45)",
};

const ensureLeafletCss = () => {
  const id = "leaflet-css";
  if (!document.getElementById(id)) {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);
  }
};

const fixLeafletIcon = () => {
  const base = "https://unpkg.com/leaflet@1.9.4/dist/images/";
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: base + "marker-icon-2x.png",
    iconUrl: base + "marker-icon.png",
    shadowUrl: base + "marker-shadow.png",
  });
};

type Stadium = {
  id: string;
  name: string;
  city?: string;
  country?: string;
  lat: number;
  lng: number;
  capacity?: number;
};

type MatchRow = {
  date: string;
  home_team: string;
  away_team: string;
  home_score: number | string;
  away_score: number | string;
  competition?: string;
  stadium?: string;
  city?: string;
  country?: string;
  neutral?: boolean | string;
  world_cup?: boolean | string;
  [k: string]: any;
};

/* Flag path helper is imported from utils/flags (rugbyFlagPath) */

// Legend ordering labels (kept from your countryCode approach)
const countryCode = (country?: string) => {
  const c = (country || "").trim().toLowerCase();
  if (!c) return "OT";
  if (c.includes("england")) return "EN";
  if (c.includes("scotland")) return "SC";
  if (c.includes("wales")) return "WL";
  if (c.includes("northern ireland")) return "NI";
  if (c.includes("ireland")) return "IE";
  if (c.includes("france")) return "FR";
  if (c.includes("italy")) return "IT";
  if (c.includes("new zealand")) return "NZ";
  if (c.includes("australia")) return "AU";
  if (c.includes("south africa")) return "ZA";
  if (c.includes("argentina")) return "AR";
  if (c.includes("japan")) return "JP";
  return (country || "Other").slice(0, 2).toUpperCase();
};

// Build a Leaflet divIcon using a flag image instead of colored dot
const makeFlagIcon = (flagSrc: string, alt: string) => {
  const h = 22;
  const w = Math.round(h * 1.6);

  const html = `
    <div style="
      width:${w}px;height:${h}px;
      border-radius:6px;
      overflow:hidden;
      border:2px solid rgba(255,255,255,0.95);
      box-shadow:0 2px 8px rgba(0,0,0,0.20);
      background:#fff;
      display:flex;align-items:center;justify-content:center;
    ">
      <img src="${flagSrc}" alt="${alt}" style="
        width:100%;height:100%;
        object-fit:cover;
        display:block;
      "/>
    </div>
  `;

  return L.divIcon({
    className: "pin-flag-icon",
    html,
    iconSize: [w, h],
    iconAnchor: [w / 2, h / 2],
    popupAnchor: [0, -h / 2],
    tooltipAnchor: [0, -h / 2],
  });
};

const flagIconCache = new Map<string, L.DivIcon>();
const getIconForStadium = (s: Stadium) => {
  const src = rugbyFlagPath(s.country);
  const key = `${countryCode(s.country)}:${src}`;
  if (!flagIconCache.has(key)) {
    flagIconCache.set(key, makeFlagIcon(src, s.country || "flag"));
  }
  return flagIconCache.get(key)!;
};

// ===== WORLD CLAMP (prevents grey screen) =====
const WORLD_BOUNDS = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));

/**
 * Map guard:
 * - Ensures ONLY ONE zoom control exists (bottom-right)
 * - Clamps panning to world bounds (no blank tiles)
 */
const MapGuards: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const anyMap = map as any;

    // Remove any existing zoom controls (hot-reload safe)
    const controls: any[] = anyMap._controls || [];
    controls
      .filter((c) => c && c instanceof L.Control.Zoom)
      .forEach((c) => {
        try {
          map.removeControl(c);
        } catch {
          // ignore
        }
      });

    try {
      if (anyMap.zoomControl) anyMap.zoomControl.remove();
    } catch {
      // ignore
    }

    // Add one zoom control bottom-right
    const zoom = L.control.zoom({ position: "bottomright" });
    zoom.addTo(map);

    // Clamp bounds
    map.setMaxBounds(WORLD_BOUNDS);

    const onDrag = () => {
      try {
        map.panInsideBounds(WORLD_BOUNDS, { animate: false } as any);
      } catch {
        // ignore
      }
    };
    map.on("drag", onDrag);

    return () => {
      map.off("drag", onDrag);
      try {
        map.removeControl(zoom);
      } catch {
        // ignore
      }
    };
  }, [map]);

  return null;
};

const FitBounds: React.FC<{ stadiums: Stadium[] }> = ({ stadiums }) => {
  const map = useMap();

  useEffect(() => {
    if (!stadiums.length) return;

    let cancelled = false;

    const run = () => {
      if (cancelled) return;

      const anyMap = map as any;
      if (!anyMap || !anyMap._mapPane) return;

      const bounds = L.latLngBounds(
        stadiums.map((s) => [s.lat, s.lng] as [number, number])
      );

      try {
        map.fitBounds(bounds, { padding: [40, 40], animate: false } as any);
        map.panInsideBounds(WORLD_BOUNDS, { animate: false } as any);
      } catch {
        // ignore
      }
    };

    map.whenReady(run);

    return () => {
      cancelled = true;
    };
  }, [stadiums, map]);

  return null;
};

type Outcome = "HOME" | "AWAY" | "DRAW" | "NA";

const safeNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getOutcome = (m: MatchRow): { outcome: Outcome; winner: string | null } => {
  const hs = safeNum(m.home_score);
  const as = safeNum(m.away_score);
  if (hs === null || as === null) return { outcome: "NA", winner: null };
  if (hs === as) return { outcome: "DRAW", winner: "Draw" };
  if (hs > as) return { outcome: "HOME", winner: String(m.home_team) };
  return { outcome: "AWAY", winner: String(m.away_team) };
};

const pillSx = (variant: "WIN" | "LOSE" | "DRAW" | "NEUTRAL") => {
  if (variant === "WIN")
    return {
      bgcolor: BRAND.winBg,
      border: `1px solid ${BRAND.winBorder}`,
      color: BRAND.winInk,
    };
  if (variant === "LOSE")
    return {
      bgcolor: BRAND.loseBg,
      border: `1px solid ${BRAND.loseBorder}`,
      color: BRAND.ink,
    };
  if (variant === "DRAW")
    return {
      bgcolor: BRAND.drawBg,
      border: `1px solid ${BRAND.drawBorder}`,
      color: BRAND.ink,
    };
  return {
    bgcolor: BRAND.chipBg,
    border: `1px solid ${BRAND.border}`,
    color: BRAND.ink,
  };
};

const TeamScorePill: React.FC<{
  team: string;
  score: number | string;
  variant: "WIN" | "LOSE" | "DRAW" | "NEUTRAL";
  showIcon?: boolean;
}> = ({ team, score, variant, showIcon = true }) => {
  const isWin = variant === "WIN";
  const isDraw = variant === "DRAW";
  return (
    <Box
      sx={{
        ...pillSx(variant),
        borderRadius: 2,
        px: 1.1,
        py: 0.7,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.8,
        minWidth: 0,
        boxShadow: "0 8px 16px rgba(23,107,135,0.10)",
      }}
    >
      {showIcon && isWin && (
        <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: "rgba(11,34,48,0.85)" }} />
      )}
      {showIcon && isDraw && (
        <SportsRugbyOutlinedIcon sx={{ fontSize: 18, color: "rgba(11,34,48,0.85)" }} />
      )}

      <Typography
        sx={{
          fontWeight: 950,
          fontSize: 13,
          color: BRAND.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 190,
        }}
        title={team}
      >
        {team}
      </Typography>

      <Box
        sx={{
          ml: 0.6,
          px: 0.9,
          py: 0.2,
          borderRadius: 999,
          fontWeight: 1000,
          background: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(134,182,246,0.75)",
          color: BRAND.ink,
          lineHeight: 1.1,
          flex: "0 0 auto",
        }}
      >
        {typeof score === "number" || String(score).trim() ? score : "-"}
      </Box>

      {showIcon && isWin && (
        <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18, color: "rgba(46, 204, 113, 0.95)" }} />
      )}
    </Box>
  );
};

type DrawerView = "COUNTRY" | "STADIUM";

/* =========================
   Robust stadium matching (kept)
========================= */
const normalize = (s: any) => {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(
      /\b(stadium|arena|ground|park|field|oval|the|at|of|national|international)\b/g,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();
};

const includesEither = (a: string, b: string) => {
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
};

// City coordinate fallbacks (ONLY used if stadiums.json does not provide the venue)
const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  "tokyo,japan": { lat: 35.6762, lng: 139.6503 },
  "melbourne,australia": { lat: -37.8136, lng: 144.9631 },
  "sydney,australia": { lat: -33.8688, lng: 151.2093 },
  "auckland,new zealand": { lat: -36.8509, lng: 174.7645 },
  "osaka,japan": { lat: 34.6937, lng: 135.5023 },
  "yokohama,japan": { lat: 35.4437, lng: 139.638 },
  "wellington,new zealand": { lat: -41.2866, lng: 174.7756 },
};

const hasCoords = (s: any) =>
  Number.isFinite(Number(s?.lat)) && Number.isFinite(Number(s?.lng));

const getMatchesForStadium = (
  s: Stadium,
  matches: MatchRow[],
  opts?: { worldCupOnly?: boolean }
) => {
  const worldCupOnly = !!opts?.worldCupOnly;

  const sNameN = normalize(s.name);
  const sCityN = normalize(s.city);
  const sCountryN = normalize(s.country);

  return matches
    .filter((m) => {
      if (worldCupOnly && !m.world_cup) return false;

      const mNameN = normalize(m.stadium);
      const mCityN = normalize(m.city);
      const mCountryN = normalize(m.country);

      const stadiumOk = !!sNameN && !!mNameN && includesEither(mNameN, sNameN);

      const cityOk = !!sCityN && !!mCityN && includesEither(mCityN, sCityN);
      const countryOk = !!sCountryN && !!mCountryN && includesEither(mCountryN, sCountryN);

      const locOk = (cityOk && (countryOk || !sCountryN)) || (countryOk && !sCityN);

      return stadiumOk || locOk;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

const inferVenuesFromMatches = (matches: MatchRow[]): Stadium[] => {
  const wc = matches.filter((m) => !!m.world_cup);
  const byKey = new Map<
    string,
    { stadium?: string; city?: string; country?: string; count: number }
  >();

  for (const m of wc) {
    const st = String(m.stadium || "").trim();
    const city = String(m.city || "").trim();
    const country = String(m.country || "").trim();

    const key = `${normalize(st)}|${normalize(city)}|${normalize(country)}`;
    if (!key.replace(/\|/g, "")) continue;

    const prev = byKey.get(key);
    if (!prev)
      byKey.set(key, {
        stadium: st || undefined,
        city: city || undefined,
        country: country || undefined,
        count: 1,
      });
    else prev.count += 1;
  }

  const inferred: Stadium[] = [];
  for (const [key, v] of byKey.entries()) {
    const ck = `${normalize(v.city)},${normalize(v.country)}`.replace(/\s+/g, " ").trim();
    const coord = CITY_COORDS[ck];
    if (!coord) continue;

    inferred.push({
      id: `gen:${key}`,
      name: v.stadium || v.city || "Unknown Venue",
      city: v.city,
      country: v.country,
      lat: coord.lat,
      lng: coord.lng,
    });
  }

  return inferred;
};

const WorldCup: React.FC = () => {
  const { rows: rawRows } = useData();
  const [stadiums, setStadiums] = useState<Stadium[]>([]);
  const [stadiumsLoadError, setStadiumsLoadError] = useState(false);
  const [selected, setSelected] = useState<Stadium | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [drawerView, setDrawerView] = useState<DrawerView>("COUNTRY");
  const [legendOpen, setLegendOpen] = useState(true);

  const [vw, setVw] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = vw < 700;

  const headerOffsetPx = isMobile ? 56 : 64;

  useEffect(() => {
    setLegendOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    ensureLeafletCss();
    fixLeafletIcon();
  }, []);

  useEffect(() => {
    fetch("/data/stadiums.json")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: Stadium[]) => setStadiums(Array.isArray(json) ? json : []))
      .catch(() => {
        setStadiums([]);
        setStadiumsLoadError(true);
      });
  }, []);

  const toBool = (v: any) => {
    if (typeof v === "boolean") return v;
    if (v == null) return false;
    const s = String(v).trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  };

  const matches = useMemo<MatchRow[]>(() => {
    return rawRows
      .filter((r) => r?.date && r?.home_team && r?.away_team)
      .map((r) => ({
        ...r,
        date: String(r.date),
        home_team: String(r.home_team),
        away_team: String(r.away_team),
        stadium: r.stadium != null ? String(r.stadium) : "",
        city: r.city != null ? String(r.city) : "",
        country: r.country != null ? String(r.country) : "",
        neutral: toBool(r.neutral),
        world_cup: toBool(r.world_cup),
      })) as MatchRow[];
  }, [rawRows]);

  const mergedStadiums: Stadium[] = useMemo(() => {
    const base = (stadiums || []).filter(hasCoords).map((s) => ({
      ...s,
      lat: Number(s.lat),
      lng: Number(s.lng),
    }));

    const inferred = inferVenuesFromMatches(matches);

    const seen = new Set(
      base.map((s) => `${normalize(s.name)}|${normalize(s.city)}|${normalize(s.country)}`)
    );

    const extra = inferred.filter((s) => {
      const k = `${normalize(s.name)}|${normalize(s.city)}|${normalize(s.country)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    return [...base, ...extra];
  }, [stadiums, matches]);

  const wcStadiums = useMemo(() => {
    if (!mergedStadiums.length) return [];
    if (!matches.length) return [];
    return mergedStadiums.filter(
      (s) => getMatchesForStadium(s, matches, { worldCupOnly: true }).length > 0
    );
  }, [mergedStadiums, matches]);

  // Default selection: stadium with most WC matches
  useEffect(() => {
    if (selected || !wcStadiums.length) return;

    let best: Stadium | null = null;
    let bestCount = -1;

    for (const s of wcStadiums) {
      const c = getMatchesForStadium(s, matches, { worldCupOnly: true }).length;
      if (c > bestCount) {
        bestCount = c;
        best = s;
      }
    }

    if (best) setSelected(best);
  }, [wcStadiums, matches, selected]);

  const selectedCountry = useMemo(() => (selected?.country || "").trim(), [selected]);

  const stadiumsInCountry = useMemo(() => {
    if (!selectedCountry) return [];
    const c = selectedCountry.toLowerCase().trim();
    return wcStadiums
      .filter((s) => (s.country || "").toLowerCase().trim() === c)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [wcStadiums, selectedCountry]);

  const matchesAtStadium = useMemo(() => {
    if (!selected) return [];
    return getMatchesForStadium(selected, matches, { worldCupOnly: true });
  }, [selected, matches]);

  const filteredList = useMemo(() => {
    if (!search) return matchesAtStadium;
    const q = search.toLowerCase();
    return matchesAtStadium.filter(
      (m) =>
        String(m.home_team).toLowerCase().includes(q) ||
        String(m.away_team).toLowerCase().includes(q) ||
        String(m.date).toLowerCase().includes(q) ||
        String(m.competition || "").toLowerCase().includes(q) ||
        String(m.stadium || "").toLowerCase().includes(q) ||
        String(m.city || "").toLowerCase().includes(q) ||
        String(m.country || "").toLowerCase().includes(q)
    );
  }, [matchesAtStadium, search]);

  const onMarkerClick = (s: Stadium) => {
    setSelected(s);
    setDrawerView("COUNTRY");
    setDrawerOpen(true);
  };

  const openStadiumMatches = (s: Stadium) => {
    setSelected(s);
    setDrawerView("STADIUM");
    setDrawerOpen(true);
  };

  const goBackToCountry = () => {
    setDrawerView("COUNTRY");
  };

  const legendCodes = useMemo(() => {
    const set = new Set<string>();
    wcStadiums.forEach((s) => set.add(countryCode(s.country)));
    set.add("OT");
    const order = ["EN", "SC", "WL", "IE", "NI", "FR", "IT", "NZ", "AU", "ZA", "AR", "JP", "OT"];
    return order.filter((c) => set.has(c));
  }, [wcStadiums]);

  // Map legend code -> representative country name for flag selection
  const codeToCountryNameForFlag = (code: string): string => {
    switch (code) {
      case "EN": return "England";
      case "SC": return "Scotland";
      case "WL": return "Wales";
      case "IE": return "Ireland";
      case "NI": return "Northern Ireland";
      case "FR": return "France";
      case "IT": return "Italy";
      case "NZ": return "New Zealand";
      case "AU": return "Australia";
      case "ZA": return "South Africa";
      case "AR": return "Argentina";
      case "JP": return "Japan";
      default: return "Other";
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (drawerOpen) setDrawerOpen(false);
      else if (legendOpen && isMobile) setLegendOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, legendOpen, isMobile]);

  const drawerWidth = isMobile ? "100vw" : 480;

  useEffect(() => {
    if (drawerView === "STADIUM") setSearch("");
  }, [drawerView, selected]);

  return (
    <Box
      sx={{
        height: `calc(100vh - ${headerOffsetPx}px)`,
        position: "relative",
        background: BRAND.bg,
      }}
    >
      {stadiumsLoadError && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2000,
            bgcolor: "rgba(255,255,255,0.96)",
            border: "1px solid rgba(138,31,31,0.35)",
            borderRadius: 2,
            px: 2,
            py: 1,
            fontSize: 13,
            color: "#8A1F1F",
            fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            pointerEvents: "none",
          }}
        >
          Could not load stadium data — map markers may be incomplete
        </Box>
      )}
      <MapContainer
        center={[20, 0]}
        zoom={2}
        minZoom={2}
        zoomControl={false}
        maxBounds={WORLD_BOUNDS as any}
        maxBoundsViscosity={1.0}
        style={{ height: "100%", width: "100%" }}
        worldCopyJump
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
      >
        <MapGuards />

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OSM"
        />

        <FitBounds stadiums={wcStadiums} />

        {wcStadiums.map((s) => (
          <Marker
            key={s.id}
            position={[s.lat, s.lng]}
            icon={getIconForStadium(s)} // ✅ now flags instead of dots
            eventHandlers={{ click: () => onMarkerClick(s) }}
          >
            <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
              <div style={{ fontWeight: 800 }}>{s.name}</div>
              <div style={{ fontSize: 12 }}>
                {[s.city, s.country].filter(Boolean).join(", ")}
              </div>
              {s.capacity && (
                <div style={{ fontSize: 12 }}>
                  Capacity: {s.capacity.toLocaleString()}
                </div>
              )}
            </Tooltip>

            <Popup>
              <div style={{ minWidth: 240 }}>
                <div style={{ fontWeight: 900, color: BRAND.ink }}>{s.name}</div>
                {[s.city, s.country].some(Boolean) && (
                  <div style={{ color: "rgba(11,34,48,0.72)" }}>
                    {[s.city, s.country].filter(Boolean).join(", ")}
                  </div>
                )}
                {s.capacity ? <div>Capacity: {s.capacity.toLocaleString()}</div> : null}

                <Divider sx={{ my: 1, borderColor: "rgba(134,182,246,0.6)" }} />

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    color: "rgba(11,34,48,0.8)",
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                  <span>Open the match list for this stadium.</span>
                </div>

                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <MuiTooltip title="Explore (map is clamped + fit bounds enabled)">
                    <IconButton size="small" aria-label="Explore">
                      <ExploreIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>

                  <button
                    onClick={() => openStadiumMatches(s)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      border: `1px solid ${BRAND.border}`,
                      background: BRAND.panelBg,
                      fontWeight: 900,
                      cursor: "pointer",
                      color: BRAND.ink,
                    }}
                  >
                    View Matches
                  </button>
                </Box>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Legend (top-left) */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            pointerEvents: "auto",
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2,
            boxShadow: "0 10px 22px rgba(23,107,135,0.12)",
            minWidth: legendOpen ? 220 : 120,
            maxWidth: isMobile ? 280 : 340,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: legendOpen ? `1px solid rgba(134,182,246,0.5)` : "none",
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 12, color: BRAND.ink }}>
              {legendOpen ? "Pin Legend (Country)" : "Legend"}
            </Typography>

            <MuiTooltip title={legendOpen ? "Collapse legend" : "Expand legend"}>
              <IconButton
                size="small"
                onClick={() => setLegendOpen((v) => !v)}
                sx={{
                  borderRadius: 2,
                  border: `1px solid rgba(134,182,246,0.65)`,
                  background: "rgba(238,245,255,0.6)",
                }}
                aria-label={legendOpen ? "Collapse legend" : "Expand legend"}
              >
                <span
                  style={{
                    display: "inline-block",
                    transform: legendOpen ? "rotate(180deg)" : "rotate(0deg)",
                    fontWeight: 900,
                    lineHeight: 1,
                    color: BRAND.ink,
                  }}
                >
                  ▾
                </span>
              </IconButton>
            </MuiTooltip>
          </Box>

          {legendOpen && (
            <Box
              sx={{
                p: 1.25,
                display: "grid",
                gap: 0.75,
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              }}
            >
              {legendCodes.map((code) => {
                const label = code;
                const countryName = codeToCountryNameForFlag(code);
                const flagSrc = rugbyFlagPath(countryName);
                return (
                  <Box
                    key={code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 2,
                      border: `1px solid rgba(134,182,246,0.55)`,
                      background: "rgba(238,245,255,0.55)",
                    }}
                  >
                    <img
                      src={flagSrc}
                      alt={countryName}
                      width={28}
                      height={18}
                      style={{
                        display: "block",
                        borderRadius: 6,
                        objectFit: "cover",
                        border: "1px solid rgba(255,255,255,0.95)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                      }}
                    />
                    <Typography sx={{ fontSize: 12, color: BRAND.ink, fontWeight: 900 }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: drawerWidth,
            background: BRAND.panelBg,
            borderLeft: `1px solid ${BRAND.border}`,
            height: "100%",
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 950,
                color: BRAND.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={
                drawerView === "COUNTRY"
                  ? selectedCountry || "Country"
                  : selected?.name || "Stadium"
              }
            >
              {drawerView === "COUNTRY"
                ? selectedCountry || "Country"
                : selected
                ? selected.name
                : "Stadium"}
            </Typography>

            {drawerView === "COUNTRY" ? (
              <Typography sx={{ mt: 0.2, fontSize: 12, color: "rgba(11,34,48,0.72)" }}>
                {stadiumsInCountry.length} stadium{stadiumsInCountry.length !== 1 ? "s" : ""} mapped
              </Typography>
            ) : (
              selected && (
                <Typography
                  sx={{
                    mt: 0.2,
                    fontSize: 12,
                    color: "rgba(11,34,48,0.72)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <SouthAmericaOutlinedIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                  {[selected.city, selected.country].filter(Boolean).join(", ") || "—"}
                </Typography>
              )
            )}
          </Box>

          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: "rgba(134,182,246,0.6)" }} />

        {/* COUNTRY VIEW */}
        {selected && drawerView === "COUNTRY" && (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 1 }}>
              Stadiums in {selectedCountry || "—"}
            </Typography>

            <Box sx={{ display: "grid", gap: 1 }}>
              {stadiumsInCountry.map((s) => (
                <Box
                  key={s.id}
                  onClick={() => {
                    setSelected(s);
                    setDrawerView("STADIUM");
                    setDrawerOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                >
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 1.2,
                      border: `1px solid ${BRAND.border}`,
                      boxShadow: "0 10px 22px rgba(23,107,135,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 950, color: BRAND.ink }} noWrap>
                        {s.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "rgba(11,34,48,0.72)" }} noWrap>
                        {[s.city, s.country].filter(Boolean).join(", ") || "—"}
                      </Typography>
                    </Box>

                    {s.capacity ? (
                      <Chip
                        label={`Cap ${s.capacity.toLocaleString()}`}
                        size="small"
                        sx={{
                          background: BRAND.chipBg,
                          border: `1px solid ${BRAND.border}`,
                          fontWeight: 900,
                          flex: "0 0 auto",
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* STADIUM VIEW */}
        {selected && drawerView === "STADIUM" && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 1 }}>
              <Chip
                icon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                label="Back"
                onClick={goBackToCountry}
                clickable
                sx={{
                  background: "rgba(255,255,255,0.7)",
                  border: `1px solid ${BRAND.border}`,
                  fontWeight: 900,
                  "& .MuiChip-icon": { color: BRAND.accent },
                }}
              />
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
              {selected.city && (
                <Chip
                  label={selected.city}
                  sx={{
                    background: BRAND.chipBg,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 900,
                  }}
                />
              )}
              {selected.country && (
                <Chip
                  label={selected.country}
                  sx={{
                    background: BRAND.chipBg,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 900,
                  }}
                />
              )}
              {selected.capacity && (
                <Chip
                  label={`Cap ${selected.capacity.toLocaleString()}`}
                  sx={{
                    background: BRAND.chipBg,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 900,
                  }}
                />
              )}
            </Stack>

            <TextField
              fullWidth
              size="small"
              placeholder="Search matches (team/date/competition)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 0,
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  borderRadius: 2,
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75, color: BRAND.ink, fontWeight: 900 }}>
                {filteredList.length} match{filteredList.length !== 1 ? "es" : ""} at this venue
              </Typography>

              <MuiTooltip title="Winner is highlighted in green. Draws are yellow.">
                <Chip
                  size="small"
                  icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 16 }} />}
                  label="Legend"
                  sx={{
                    background: "rgba(255,255,255,0.7)",
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 900,
                    "& .MuiChip-icon": { color: BRAND.accent },
                  }}
                />
              </MuiTooltip>
            </Box>

            <Divider sx={{ mb: 1.2, borderColor: "rgba(134,182,246,0.6)" }} />

            <Box sx={{ display: "grid", gap: 1.2 }}>
              {filteredList.map((m, idx) => {
                const hs = safeNum(m.home_score);
                const as = safeNum(m.away_score);
                const { outcome, winner } = getOutcome(m);

                const homeVariant =
                  outcome === "HOME"
                    ? "WIN"
                    : outcome === "AWAY"
                    ? "LOSE"
                    : outcome === "DRAW"
                    ? "DRAW"
                    : "NEUTRAL";
                const awayVariant =
                  outcome === "AWAY"
                    ? "WIN"
                    : outcome === "HOME"
                    ? "LOSE"
                    : outcome === "DRAW"
                    ? "DRAW"
                    : "NEUTRAL";

                return (
                  <Box
                    key={`${m.date}-${m.home_team}-${m.away_team}-${idx}`}
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 1.2,
                      border: `1px solid ${BRAND.border}`,
                      boxShadow: "0 10px 22px rgba(23,107,135,0.12)",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "1fr" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <TeamScorePill team={String(m.home_team)} score={hs ?? "-"} variant={homeVariant as any} />
                        <Typography sx={{ fontWeight: 950, color: "rgba(11,34,48,0.65)" }}>vs</Typography>
                        <TeamScorePill team={String(m.away_team)} score={as ?? "-"} variant={awayVariant as any} />
                      </Box>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                        <Chip
                          size="small"
                          label={String(m.date)}
                          sx={{
                            background: "rgba(238,245,255,0.75)",
                            border: `1px solid rgba(134,182,246,0.6)`,
                            fontWeight: 900,
                          }}
                        />

                        {m.competition && (
                          <Chip
                            size="small"
                            label={String(m.competition)}
                            sx={{
                              background: "rgba(238,245,255,0.75)",
                              border: `1px solid rgba(134,182,246,0.6)`,
                              fontWeight: 900,
                            }}
                          />
                        )}

                        {!!m.neutral && (
                          <Chip
                            size="small"
                            label="Neutral"
                            sx={{
                              background: BRAND.chipBg,
                              border: `1px solid ${BRAND.border}`,
                              fontWeight: 900,
                            }}
                          />
                        )}

                        {!!m.world_cup && (
                          <Chip
                            size="small"
                            label="World Cup"
                            sx={{
                              background: BRAND.chipBg,
                              border: `1px solid ${BRAND.border}`,
                              fontWeight: 900,
                            }}
                          />
                        )}
                      </Box>

                      {(m.stadium || m.city || m.country) && (
                        <Typography sx={{ fontSize: 12, color: "rgba(11,34,48,0.72)" }}>
                          {[
                            m.stadium ? `Stadium: ${m.stadium}` : "",
                            m.city ? `City: ${m.city}` : "",
                            m.country ? `Country: ${m.country}` : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      )}

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "rgba(11,34,48,0.72)",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <EmojiEventsOutlinedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                        Winner: <strong>{winner || "-"}</strong>
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default WorldCup;
